import {
  doc, getDoc, collection, query, where, getDocs, onSnapshot,
  updateDoc, deleteDoc, orderBy,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import type {
  LearningPath, NodeContent, CheckpointScore, GenerationStatus,
} from '../types/learning-path';

// Lazy functions instance
let _functions: ReturnType<typeof import('firebase/functions').getFunctions> | null = null;
async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions');
    const { app } = await import('./firebase');
    _functions = getFunctions(app);
  }
  return _functions;
}

// ─── Generation ───

export async function triggerPathGeneration(contentId: string): Promise<string> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ contentId: string }, { pathId: string; nodeCount: number }>(
    functions, 'generateLearningPath',
  );
  const result = await fn({ contentId });
  return result.data.pathId;
}

export async function triggerNodeContentGeneration(pathId: string): Promise<void> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable(functions, 'generateNodeContent');
  await fn({ pathId });
}

export type ExportFormat = 'pdf_map' | 'cheat_sheet' | 'tutor_log' | 'audio' | 'flashcards';

export interface PathExportResult {
  filename: string;
  mimeType: string;
  base64: string;
}

/**
 * Server-renders a learning path to a downloadable file (currently PDF) and
 * returns it base64-encoded. Tier gating is enforced server-side.
 */
export async function exportLearningPath(
  pathId: string,
  format: ExportFormat,
): Promise<PathExportResult> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ pathId: string; format: ExportFormat }, PathExportResult>(
    functions, 'exportLearningPath',
  );
  const result = await fn({ pathId, format });
  return result.data;
}

export async function triggerVideoGeneration(pathId: string): Promise<void> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable(functions, 'generateVideoQueries');
  await fn({ pathId });
}

// ─── Loading ───

export async function loadLearningPath(pathId: string): Promise<LearningPath | null> {
  const ref = doc(db, 'learningPaths', pathId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;
  return snapshot.data() as LearningPath;
}

export async function loadNodeContent(pathId: string, nodeId: string): Promise<NodeContent | null> {
  const ref = doc(db, 'learningPaths', pathId, 'nodes', nodeId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;
  return snapshot.data() as NodeContent;
}

export async function getPathForContent(
  contentId: string,
  userId: string,
): Promise<LearningPath | null> {
  const q = query(
    collection(db, 'learningPaths'),
    where('contentId', '==', contentId),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return snapshot.docs[0].data() as LearningPath;
}

export async function getUserLearningPaths(userId: string): Promise<LearningPath[]> {
  const q = query(
    collection(db, 'learningPaths'),
    where('userId', '==', userId),
    orderBy('lastAccessedAt', 'desc'),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => d.data() as LearningPath);
}

// ─── Real-time listeners ───

export function onGenerationProgress(
  pathId: string,
  callback: (status: GenerationStatus, progress: { totalNodes: number; completedNodes: number } | null) => void,
): () => void {
  const ref = doc(db, 'learningPaths', pathId);
  return onSnapshot(ref, (snapshot) => {
    if (!snapshot.exists()) return;
    const data = snapshot.data();
    callback(
      data.generationStatus as GenerationStatus,
      data.generationProgress as { totalNodes: number; completedNodes: number } | null,
    );
  });
}

// ─── Progress tracking ───

export async function updateProgress(
  pathId: string,
  nodeId: string,
  action: 'complete' | 'visit',
): Promise<void> {
  const ref = doc(db, 'learningPaths', pathId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return;

  const data = snapshot.data();
  const progress = data.progress as LearningPath['progress'];

  if (action === 'visit') {
    await updateDoc(ref, {
      'progress.currentNodeId': nodeId,
      lastAccessedAt: new Date().toISOString(),
    });
  } else if (action === 'complete') {
    const completed = new Set(progress.completedNodes);
    completed.add(nodeId);
    await updateDoc(ref, {
      'progress.completedNodes': Array.from(completed),
      'progress.currentNodeId': nodeId,
      lastAccessedAt: new Date().toISOString(),
    });
  }
}

// Canonical checkpoint thresholds — single source of truth for "passed"/"perfect".
export const CHECKPOINT_PASS_THRESHOLD = 70;
export const CHECKPOINT_PERFECT_THRESHOLD = 95;

/** Rollup mastery = mean of all checkpoint scores (0 when none yet). */
export function computeOverallMastery(scores: Record<string, CheckpointScore>): number {
  const vals = Object.values(scores)
    .map((s) => s.score)
    .filter((n): n is number => typeof n === 'number');
  if (vals.length === 0) return 0;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

export async function submitCheckpoint(
  pathId: string,
  nodeId: string,
  answer: string,
  checkpointData: {
    question: string;
    questionType: string;
    options: string[] | null;
    correctAnswer: string | null;
    rubric: string | null;
    conceptsTested: string[];
  },
  meta: {
    /** Seconds the learner spent before submitting. */
    timeTaken?: number;
    /** 1-based attempt number for this node. */
    attempts?: number;
    /** Existing scores, so we can roll up overallMastery in the same write. */
    existingScores?: Record<string, CheckpointScore>;
  } = {},
): Promise<CheckpointScore> {
  let rawScore: number;
  let signalClassification: CheckpointScore['signalClassification'];

  // For multiple choice, score locally (100/0).
  if (checkpointData.questionType === 'multiple_choice' && checkpointData.correctAnswer !== null) {
    const isCorrect = answer === checkpointData.correctAnswer;
    rawScore = isCorrect ? 100 : 0;
    signalClassification = isCorrect ? 'mastery_demonstrated' : 'misconception_detected';
  } else {
    // For open-ended, call RT-003 via the Claude proxy.
    const { callClaude } = await import('./claude');
    const response = await callClaude(
      `You are CogniPrint's checkpoint scorer. Score the learner's response.
SCORING: Multiple choice = 100 or 0. Open-ended = 0-100 (accuracy 40%, depth 30%, examples 30%).
FEEDBACK: Correct = affirming + specific. Incorrect = honest + encouraging. Partial = acknowledge right + address gap.
SIGNAL: ≥85 = mastery_demonstrated, 50-84 = needs_reinforcement, <50 = misconception_detected.
OUTPUT: JSON only.`,
      [{
        role: 'user',
        content: `Score this checkpoint.
Question: ${checkpointData.question}
Type: ${checkpointData.questionType}
Rubric: ${checkpointData.rubric ?? 'N/A'}
Concepts: ${JSON.stringify(checkpointData.conceptsTested)}
Learner response: ${answer}

Respond with JSON: { "score": <0-100>, "signal_classification": "<string>", "feedback": { "summary": "<string>" } }`,
      }],
      { model: 'haiku', maxTokens: 512 },
    );

    const jsonMatch = response.match(/\{[\s\S]*\}/);
    const result = jsonMatch ? JSON.parse(jsonMatch[0]) : { score: 50, signal_classification: 'needs_reinforcement' };
    rawScore = typeof result.score === 'number' ? result.score : 50;
    signalClassification = result.signal_classification as CheckpointScore['signalClassification'];
  }

  const score: CheckpointScore = {
    nodeId,
    score: rawScore,
    timestamp: new Date().toISOString(),
    timeTaken: Math.max(0, Math.round(meta.timeTaken ?? 0)),
    attempts: Math.max(1, Math.round(meta.attempts ?? 1)),
    signalClassification,
  };

  const allScores = { ...(meta.existingScores ?? {}), [nodeId]: score };
  await updateDoc(doc(db, 'learningPaths', pathId), {
    [`progress.checkpointScores.${nodeId}`]: score,
    'progress.overallMastery': computeOverallMastery(allScores),
  });

  return score;
}

// ─── Path management ───

export async function regenerateNode(pathId: string, nodeId: string): Promise<void> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable(functions, 'regenerateSingleNode');
  await fn({ pathId, nodeId });
}

export async function deleteLearningPath(pathId: string): Promise<void> {
  await deleteDoc(doc(db, 'learningPaths', pathId));
}

export async function archiveLearningPath(pathId: string, archived: boolean): Promise<void> {
  await updateDoc(doc(db, 'learningPaths', pathId), {
    archived,
    archivedAt: archived ? new Date().toISOString() : null,
  });
}

export async function stopLearningPath(pathId: string): Promise<void> {
  await updateDoc(doc(db, 'learningPaths', pathId), {
    generationStatus: 'error',
  });
}

export async function resumeLearningPath(pathId: string): Promise<void> {
  // Re-trigger by setting status back — the Cloud Function will pick it up
  await updateDoc(doc(db, 'learningPaths', pathId), {
    generationStatus: 'generating_content',
  });
}
